/**
 * GenericManager — handles News, Projects, Trainings, Events
 *
 * Data shape (news):       { news: [{titulli, teksti, data, foto}] }
 * Data shape (projects…):  { projects: { title, mainTitle, elements: [{title/titulli, description/teksti, data, foto, images}] } }
 */
import React, { Component } from 'react';
import AdminLayout from '../AdminLayout.jsx';
import { fetchDataFile, saveDataFile, uploadPhotoFile } from '../../../APICall/Api.jsx';

const CONFIGS = {
  news: {
    file:    'newsEng.json',
    fileAlb: 'newsAlb.json',
    title:   'News',
    rootKey: 'news',        // result[rootKey] is the array
    flat:    true,          // array directly (not .elements)
    fields: [
      { key: 'titulli', label: 'Title',   type: 'text' },
      { key: 'data',    label: 'Date',    type: 'text' },
      { key: 'foto',    label: 'Image URL', type: 'image' },
      { key: 'teksti',  label: 'Content', type: 'textarea' },
    ],
  },
  projects: {
    file:    'ProjectsEng.json',
    fileAlb: 'ProjectsAlb.json',
    title:   'Projects',
    rootKey: 'projects',
    flat:    false,         // result[rootKey].elements is the array
    fields: [
      { key: 'title',  label: 'Title',   type: 'text' },
      { key: 'data',   label: 'Date',    type: 'text' },
      { key: 'foto',   label: 'Image URL', type: 'image' },
      { key: 'description', label: 'Content', type: 'textarea' },
    ],
  },
  trainings: {
    file:    'TrainingsEng.json',
    fileAlb: 'TrainingsAlb.json',
    title:   'Trainings',
    rootKey: 'trainings',
    flat:    false,
    fields: [
      { key: 'title',  label: 'Title',   type: 'text' },
      { key: 'data',   label: 'Date',    type: 'text' },
      { key: 'foto',   label: 'Image URL', type: 'image' },
      { key: 'description', label: 'Content', type: 'textarea' },
    ],
  },
  events: {
    file:    'EventsEng.json',
    fileAlb: 'EventsAlb.json',
    title:   'Events',
    rootKey: 'events',
    flat:    false,
    fields: [
      { key: 'title',  label: 'Title',   type: 'text' },
      { key: 'data',   label: 'Date',    type: 'text' },
      { key: 'foto',   label: 'Image URL', type: 'image' },
      { key: 'description', label: 'Content', type: 'textarea' },
    ],
  },
};

const emptyItem = (fields) => {
  const obj = {};
  fields.forEach((f) => { obj[f.key] = ''; });
  return obj;
};

class GenericManager extends Component {
  constructor(props) {
    super(props);
    const type = props.match ? props.match.params.type : props.type;
    const cfg = CONFIGS[type] || CONFIGS.news;
    this.state = {
      cfg,
      type,
      rawData: null,
      items: [],
      loading: true,
      saving: false,
      error: null,
      modal: null,       // null | 'add' | 'edit'
      editIndex: null,
      formData: emptyItem(cfg.fields),
      uploadingImg: false,
      savedMsg: '',
    };
  }

  componentDidMount() { this.loadData(); }
  componentWillReceiveProps(next) {
    const type = next.match ? next.match.params.type : next.type;
    if (type !== this.state.type) {
      const cfg = CONFIGS[type] || CONFIGS.news;
      this.setState({ cfg, type, items: [], loading: true, rawData: null }, this.loadData);
    }
  }

  loadData = async () => {
    this.setState({ loading: true });
    try {
      const data = await fetchDataFile(this.state.cfg.file);
      const { rootKey, flat } = this.state.cfg;
      const items = flat ? (data[rootKey] || []) : ((data[rootKey] && data[rootKey].elements) || []);
      this.setState({ rawData: data, items, loading: false });
    } catch (e) {
      this.setState({ error: 'Failed to load data.', loading: false });
    }
  };

  saveData = async (newItems) => {
    this.setState({ saving: true });
    try {
      const { rawData, cfg } = this.state;
      let payload;
      if (cfg.flat) {
        payload = { ...rawData, [cfg.rootKey]: newItems };
      } else {
        payload = {
          ...rawData,
          [cfg.rootKey]: { ...rawData[cfg.rootKey], elements: newItems },
        };
      }
      await saveDataFile(cfg.file, payload);
      this.setState({ items: newItems, saving: false, savedMsg: 'Saved!' });
      setTimeout(() => this.setState({ savedMsg: '' }), 3000);
    } catch (e) {
      this.setState({ saving: false, error: 'Save failed. Check your admin token.' });
    }
  };

  openAdd = () => this.setState({
    modal: 'add', formData: emptyItem(this.state.cfg.fields), editIndex: null,
  });

  openEdit = (idx) => this.setState({
    modal: 'edit', editIndex: idx, formData: { ...this.state.items[idx] },
  });

  closeModal = () => this.setState({ modal: null, formData: emptyItem(this.state.cfg.fields) });

  handleField = (key, val) =>
    this.setState((s) => ({ formData: { ...s.formData, [key]: val } }));

  handleImgUpload = async (key, file) => {
    this.setState({ uploadingImg: true });
    try {
      const res = await uploadPhotoFile(file);
      if (res.path) {
        const url = `https://buildgreenalbania.org/${res.path}`;
        this.setState((s) => ({
          formData: { ...s.formData, [key]: url },
          uploadingImg: false,
        }));
      }
    } catch (e) {
      this.setState({ uploadingImg: false });
    }
  };

  handleSave = () => {
    const { items, editIndex, formData, modal } = this.state;
    let newItems;
    if (modal === 'add') {
      newItems = [formData, ...items];
    } else {
      newItems = items.map((it, i) => (i === editIndex ? formData : it));
    }
    this.saveData(newItems);
    this.closeModal();
  };

  handleDelete = (idx) => {
    if (!window.confirm('Delete this item?')) return;
    const newItems = this.state.items.filter((_, i) => i !== idx);
    this.saveData(newItems);
  };

  getTitle = (item) => item.title || item.titulli || '(No title)';
  getDate  = (item) => item.data  || item.date  || '';
  getImg   = (item) => item.foto  || item.image || '';

  render() {
    const { cfg, items, loading, saving, modal, formData, uploadingImg, savedMsg, error } = this.state;
    const { history, location } = this.props;

    return (
      <AdminLayout title={`Manage ${cfg.title}`} history={history} location={location}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            {savedMsg && <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓ {savedMsg}</span>}
            {error && <span style={{ color: '#e53935' }}>{error}</span>}
          </div>
          <button className="bgg-btn bgg-btn-primary" onClick={this.openAdd}>
            <i className="fas fa-plus" /> Add New
          </button>
        </div>

        {/* Table */}
        <div className="bgg-admin-card">
          <div className="bgg-admin-card-header">
            <h2>{cfg.title} ({loading ? '…' : items.length})</h2>
          </div>
          {loading
            ? <div style={{ padding: 32, textAlign: 'center', color: '#8a8aaa' }}>Loading…</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table className="bgg-admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Date</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ color: '#aaa', width: 40 }}>{idx + 1}</td>
                        <td>
                          {this.getImg(item)
                            ? <img src={this.getImg(item)} alt="" />
                            : <i className="fas fa-file-alt" style={{ color: '#ccc', fontSize: 24 }} />}
                        </td>
                        <td><strong>{this.getTitle(item)}</strong></td>
                        <td>{this.getDate(item)}</td>
                        <td>
                          <div className="bgg-table-actions">
                            <button className="bgg-btn bgg-btn-outline bgg-btn-sm" onClick={() => this.openEdit(idx)}>
                              <i className="fas fa-edit" />
                            </button>
                            <button className="bgg-btn bgg-btn-danger bgg-btn-sm" onClick={() => this.handleDelete(idx)}>
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {items.length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>
                    No items yet. Click "Add New" to create one.
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Add/Edit Modal */}
        {modal && (
          <div className="bgg-modal-overlay" onClick={this.closeModal}>
            <div className="bgg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bgg-modal-header">
                <h3>{modal === 'add' ? `Add ${cfg.title}` : `Edit ${cfg.title}`}</h3>
                <button className="bgg-modal-close" onClick={this.closeModal}>×</button>
              </div>
              <div className="bgg-modal-body">
                {cfg.fields.map((field) => (
                  <div className="bgg-form-group" key={field.key}>
                    <label className="bgg-form-label">{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea
                        className="bgg-form-textarea"
                        value={formData[field.key] || ''}
                        onChange={(e) => this.handleField(field.key, e.target.value)}
                        rows={5}
                      />
                    ) : field.type === 'image' ? (
                      <div>
                        <input
                          className="bgg-form-input"
                          type="text"
                          value={formData[field.key] || ''}
                          onChange={(e) => this.handleField(field.key, e.target.value)}
                          placeholder="Paste image URL or upload below"
                        />
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ fontSize: '0.82rem' }}
                            onChange={(e) => e.target.files[0] && this.handleImgUpload(field.key, e.target.files[0])}
                          />
                          {uploadingImg && <span style={{ color: '#8a8aaa', fontSize: '0.82rem' }}>Uploading…</span>}
                        </div>
                        {formData[field.key] && (
                          <img
                            src={formData[field.key]}
                            alt=""
                            style={{ marginTop: 8, height: 80, borderRadius: 6, objectFit: 'cover' }}
                          />
                        )}
                      </div>
                    ) : (
                      <input
                        className="bgg-form-input"
                        type="text"
                        value={formData[field.key] || ''}
                        onChange={(e) => this.handleField(field.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="bgg-modal-footer">
                <button className="bgg-btn bgg-btn-outline" onClick={this.closeModal}>Cancel</button>
                <button className="bgg-btn bgg-btn-primary" onClick={this.handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    );
  }
}

export default GenericManager;



// WEBPACK FOOTER //
// ./src/views/Admin/managers/GenericManager.jsx